import * as A from 'astronomy-engine';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { services, type AppServices } from '../app';
import { DEG, clamp } from '../lib/math';
import { eyeSeesAt } from '../render/nakedeye';
import { loadJSON, panel, pointingPaused, pointingPrompt, reliving, selection, settings, viewMode } from '../state';
import { darkCheckOpen, eyeSkyNow } from './DarkCheck';
import { Icon } from './icons';

// Naked eye by day: when the eye's test leaves nothing on the sky but the Sun and Moon, a quiet note says why.

/** Why Naked eye shows only the Sun and Moon now: daylight, or twilight still too bright for stars as the Sun goes
 *  down ('dusk') or comes up ('dawn'); null when a star or planet shows (or at night). */
export type DayWhy = 'day' | 'dusk' | 'dawn';
export function dayHintWhy(s: AppServices): DayWhy | null {
  const e = s.engine, sun = e.bodies.find((b) => b.id === 'Sun');
  if (!sun) return null;
  const sunEnu = e.frame.toEnu(sun.dir), sunAlt = Math.asin(clamp(sunEnu[2], -1, 1)) / DEG;
  // By night (the Sun 6° down) the brightest stars always show.
  if (sunAlt < -6) return null;
  const eye = eyeSkyNow(s), below = settings.peek().seeThroughGround;
  const seen = (mag: number, enu: number[]) => eyeSeesAt(mag, enu[0], enu[1], enu[2], eye, below);
  for (const b of e.bodies) if (b.id !== 'Sun' && b.id !== 'Moon' && seen(b.mag, e.frame.toEnu(b.dir))) return null;
  // The stars, brightest first: only those that could show anywhere on the sky.
  const cat = e.stars;
  for (let i = 0; i < cat.count && cat.mag[i] <= eye.max; i++) if (seen(cat.mag[i], e.frame.toEnu(e.starDir(i)))) return null;
  // The Sun up (its upper edge above the horizon, refraction allowed for); else in the west, going down, or east.
  return sunAlt > -0.833 ? 'day' : sunEnu[0] < 0 ? 'dusk' : 'dawn';
}

const HINT: Record<DayWhy, [string, string]> = {
  day: ['In daylight only the Sun and Moon are bright enough to see.', 'Stars appear after sunset.'],
  dusk: ['The sky is still too bright for stars.', 'The first ones appear as it darkens.'],
  dawn: ['The sky is now too bright for stars.', 'They appear again after sunset.'],
};
/** Whether the hint's second sentence holds where and when the sky is shown: the Sun gets 3° down within 12 hours (the
 *  first stars then) for 'dusk', else it sets within a day (not so in a summer with no night). */
function promised(why: DayWhy, obs: A.Observer, date: Date): boolean {
  try {
    return why === 'dusk' ? !!A.SearchAltitude(A.Body.Sun, obs, -1, date, 0.5, -3) : !!A.SearchRiseSet(A.Body.Sun, obs, -1, date, 1);
  } catch {
    return false;
  }
}

/** The note itself, above the dock (mounted in App): in Naked eye, while only the Sun and Moon are bright enough. */
export function DayHint() {
  // Looked at again every 2 s: the Sun sets and the first stars come out without any other change to react to.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 2000);
    return () => clearInterval(id);
  }, []);
  const s = services.value;
  // Not over anything else at the bottom of the screen: a card or sheet, the darkness check, the first-visit hint,
  // pointing's prompt and paused chip, or a night being relived.
  const coaching = viewMode.value === 'manual' && !loadJSON<{ v?: number }>('coached', {}).v;
  const clear = !!s && s.engine.bodies.length > 0 && settings.value.nakedEye && !panel.value && !selection.value && !darkCheckOpen.value
    && !pointingPrompt.value && !pointingPaused.value && !reliving.value && !coaching;
  const why = clear ? dayHintWhy(s) : null;
  const obs = s?.engine.frame.observer, date = s?.engine.frame.date;
  const minute = date ? Math.floor(date.getTime() / 60e3) : 0;
  const more = useMemo(() => !!why && !!obs && !!date && promised(why, obs, date), [why, minute, obs?.latitude, obs?.longitude]);
  if (!why) return null;
  return (
    <div class="coach day-hint" role="note">
      <Icon name="sun" size={17} /> {more ? HINT[why].join(' ') : HINT[why][0]}
    </div>
  );
}
